import React from "react";
//#region types
import { AuditSheetDetailsViewProps, AuditSheetSummaryItem } from "models/propTypes/accountingPropTypes";
import { TranslationKeys } from "translations";
import { WintSuperkollAPIModel } from "wint-api-client/dist/types";
//#endregion
//#region components
import { Grid } from "@mui/material";
import { DimensionsDialogFilePortal, StaticInfoMessage, TitleSmall } from "components/common";
import useFileViewerContext from "components/common/fileViewer/context/useContext";
import { TableSkeleton } from "components/common/skeletons";
import AuditSheetSummaryList from "./auditSheetSummaryList";
import { buildAuditSheetFileList, getRowIndexOfFile } from "./auditSheetFiles";
import useAuditSheetFileViewer from "../hooks/useAuditSheetFileViewer";
import { useAuditService } from "serviceHooks";
//#endregion
import cssExports from "theme/styles/main.module.scss";
import { LocalizationHelperInstance } from "utils/helpers";

const AuditSheetDetailsView: React.FC<AuditSheetDetailsViewProps> = (props) => {
  const auditService = useAuditService();
  const { auditSheetId } = props;
  const [auditSheet, setAuditSheet] = React.useState<WintSuperkollAPIModel.AuditSheet>();
  const [auditSheetSummaries, setAuditSheetSummaries] = React.useState<AuditSheetSummaryItem[]>();
  const [loading, setLoading] = React.useState(false);
  const [viewerStart, setViewerStart] = React.useState({ key: 0, index: 0 });
  const [shownFileIndex, setShownFileIndex] = React.useState<number>();

  React.useEffect(() => {
    setLoading(true);
    auditService.getAuditSheet(auditSheetId).then((res) => {
      setAuditSheet(res);
      const accountSummary = res.accountSummary;

      if (accountSummary) {
        const summaryItems: AuditSheetSummaryItem[] = [
          {
            name: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.Balance),
            value: accountSummary.accountValue,
            isBold: false,
          },
          {
            name: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.SpecAndAdjustments),
            value: accountSummary.afterAdjustments,
            isBold: false,
          },
          {
            name: LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.Difference),
            value: accountSummary.difference,
            isBold: true,
          },
        ];
        setAuditSheetSummaries(summaryItems);
      }
      setLoading(false);
    });
  }, [auditSheetId]);

  const fileList = React.useMemo(() => buildAuditSheetFileList(auditSheet?.items), [auditSheet]);
  const selectedRowIndex = getRowIndexOfFile(fileList.files, shownFileIndex);

  // The viewer only takes a start index, so jumping to a row's file remounts it there.
  const onSelectRow = (rowIndex: number) => {
    const firstFileIndex = fileList.rows[rowIndex]?.firstFileIndex;
    if (firstFileIndex === undefined) return;

    setShownFileIndex(firstFileIndex);
    setViewerStart((previous) => ({ key: previous.key + 1, index: firstFileIndex }));
  };

  const fileViewer = useAuditSheetFileViewer(auditSheetId, fileList.files, viewerStart, setShownFileIndex);

  const { currentFileDimensions, setUploadedFileDimensions } = useFileViewerContext();
  // The dialog only places its preview slot once a size is known, and an image reports its size only after
  // loading, so until then the shared A4 default keeps the slot beside the dialog. A sheet without files
  // clears the size, or the dialog stays offset beside an empty slot.
  React.useEffect(() => {
    if (!auditSheet) return;
    if (fileList.files.length === 0) setUploadedFileDimensions(undefined);
    else if (!currentFileDimensions) setUploadedFileDimensions({ width: undefined, height: undefined });
  }, [auditSheet, fileList]);

  const auditSheetItems = React.useMemo(
    () =>
      (auditSheet?.items ?? []).map(
        (item, rowIndex): AuditSheetSummaryItem => ({
          name: item.name ?? "",
          value: item.value,
          rowIndex,
          fileCount: fileList.rows[rowIndex]?.fileCount,
          isSelected: rowIndex === selectedRowIndex,
        })
      ),
    [auditSheet, fileList, selectedRowIndex]
  );

  return (
    <>
      {fileViewer && <DimensionsDialogFilePortal>{fileViewer}</DimensionsDialogFilePortal>}
      {loading ? (
        <TableSkeleton />
      ) : (
        <Grid item className={cssExports.auditSheetDetailsView} container rowGap={4}>
          {auditSheet?.comment && (
            <StaticInfoMessage className={cssExports.staticInfoMessage} fullWidth>{auditSheet.comment}</StaticInfoMessage>
          )}
          {auditSheetItems.length > 0 ? (
            <AuditSheetSummaryList items={auditSheetItems} onSelectRow={fileViewer ? onSelectRow : undefined} />
          ) : (
            auditSheet && (
              <StaticInfoMessage fullWidth>
                {LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.NoAccountSpecification)}
              </StaticInfoMessage>
            )
          )}
          {auditSheetSummaries && (
            <Grid item container direction={"column"} gap={1}>
              <TitleSmall title={LocalizationHelperInstance.getFormattedMessage(TranslationKeys.Advisor.Summation)} />
              <Grid item>
                <AuditSheetSummaryList items={auditSheetSummaries} />
              </Grid>
            </Grid>
          )}
        </Grid>
      )}
    </>
  );
};

export default AuditSheetDetailsView;
